<template>
  <v-btn :disabled="enabled" color="white" @click="isDialogVisible = true">
    <v-icon left class="mr-1">lock</v-icon>
    {{ accessLevel }}
    <v-dialog v-model="isDialogVisible" width="30vw">
      <v-card v-if="deviceConnected">
        <v-card-title>
          <span class="text-h5">Set Access Level</span>
        </v-card-title>
        <v-card-text>
          <form>
            <v-select
              v-model="selectLevel"
              label="Access level"
              item-title="name"
              item-value="level"
              :items="levels"
              variant="underlined"
            />
            <v-text-field
              v-if="isPasswordNeeded"
              v-model="password"
              label="password"
              type="password"
              variant="underlined"
              :error="passwordError"
              :error-messages="passwordErrorMsg"
              @keydown.enter="login"
            />
            <v-btn right @click="login">Login</v-btn>
          </form>
        </v-card-text>
      </v-card>
      <v-card v-else>
        <v-card-title>
          <span class="text-h5">Set Access Level</span>
        </v-card-title>
        <v-card-text>
          <span class="d-flex flex-row">
            <v-icon>usb_off</v-icon>
            <span>No Pressure Control Solutions device is connected</span>
          </span>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-btn>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useCrypto } from "@/composables/useCrypto";
import { useDelay } from "@/composables/useDelay";
import { useModbusAddress } from "@/composables/useModbusAddress";
import { AccessLevelSetting } from "@/enums/AccessLevelSetting";
import { PortState } from "@/enums/PortState";
import { AccessLevel } from "@/enums/AccessLevel";
import { useModbusStore } from "@/stores/useModbusStore";

const { modbusAddress } = useModbusAddress();
const modbusStore = useModbusStore();
const { delay } = useDelay();
const { encrypt, arraysAreEqual } = useCrypto();

const levels = [
  { name: "Normal", level: AccessLevel.normal },
  {
    name: "Advanced",
    level: AccessLevel.advanced,
    password: new Uint8Array([
      107, 181, 65, 83, 124, 171, 206, 25, 176, 146, 86, 180, 60, 9, 130, 142,
      107, 165, 182, 159, 71, 243, 216, 170, 69, 43, 34, 96, 208, 134, 201, 23,
      179, 168, 67, 47, 145, 172, 87, 248, 30, 216,
    ]),
    iv: new Uint8Array([246, 169, 129, 9, 191, 159, 210, 11, 232, 201, 93, 11]),
  },
  {
    name: "Full",
    level: AccessLevel.full,
    password: new Uint8Array([
      26, 232, 0, 67, 107, 53, 7, 27, 255, 5, 34, 122, 187, 1, 179, 247, 214,
      212, 96, 42, 128, 228, 46, 45, 53, 221, 13, 140, 100, 152, 15, 157, 95,
      59, 94, 202, 2, 180, 69, 133, 70, 239,
    ]),
    iv: new Uint8Array([47, 36, 248, 158, 102, 196, 28, 242, 6, 13, 183, 123]),
  },
  {
    name: "Manufacturer",
    level: AccessLevel.manufacturer,
    password: new Uint8Array([
      136, 155, 47, 180, 75, 231, 139, 14, 110, 49, 47, 230, 182, 222, 106, 151,
      123, 129, 251, 65, 203, 72, 235, 79, 13, 173, 40, 151, 43, 198, 132, 203,
      25, 156, 20, 119, 42, 246, 156, 60, 93, 103,
    ]),
    iv: new Uint8Array([142, 177, 92, 168, 12, 244, 14, 0, 54, 236, 87, 221]),
  },
];

const isDialogVisible = ref(false);
const selectLevel = ref<number>();
const password = ref("");
const passwordError = ref(false);

const enabled = computed(() => {
  return modbusStore.portState === PortState.connecting;
});
const deviceConnected = computed(() => {
  return modbusStore.portState === PortState.connected;
});

const accessLevel = computed(() => {
  switch (modbusStore.accessLevel) {
    case AccessLevel.normal:
      return "Normal";
    case AccessLevel.advanced:
      return "Advanced";
    case AccessLevel.full:
      return "Full";
    case AccessLevel.manufacturer:
      return "Manufacturer";
    default:
      return "Invalid AccessLevelName";
  }
});

const passwordErrorMsg = computed(() =>
  passwordError.value ? ["Password incorrect"] : [],
);

const isPasswordNeeded = computed(
  () => selectLevel.value && selectLevel.value > modbusStore.accessLevel,
);

async function login() {
  if (modbusStore.portState !== PortState.connected || !selectLevel.value) {
    return;
  }
  const level = levels[selectLevel.value - 1];
  if (isPasswordNeeded.value && level?.password && level?.iv) {
    const { ciphertext: encryptedPassword } = await encrypt(
      password.value,
      level.iv,
    );
    if (!arraysAreEqual(encryptedPassword, level.password)) {
      passwordError.value = true;
      return;
    }
  }
  passwordError.value = false;
  password.value = "";

  let valueArray: number[] = [];
  switch (level.level) {
    case AccessLevel.normal:
      valueArray = [0x00, AccessLevelSetting.normal];
      break;
    case AccessLevel.advanced:
      valueArray = [0x00, AccessLevelSetting.advanced];
      break;
    case AccessLevel.full:
      valueArray = [0x00, AccessLevelSetting.full];
      break;
    case AccessLevel.manufacturer:
      valueArray = [0x00, AccessLevelSetting.manufacturer];
      break;
    default:
      break;
  }

  for (let i = 0; i < modbusStore.maxReloadCount; i++) {
    modbusStore.modbusAddrWriteBusy[modbusAddress.accessLevel] = true;
    modbusStore.modbusWrite(
      modbusStore.deviceAddress,
      modbusAddress.accessLevel,
      1,
      new Uint8Array(valueArray),
    );
    while (modbusStore.modbusAddrWriteBusy[modbusAddress.accessLevel]) {
      await delay(1);
    }
    if (modbusStore.modbusAddrErrMsg[modbusAddress.accessLevel] === "") {
      break;
    }
  }
  // read the actual device access level back
  await modbusStore.modbusRead(
    modbusStore.deviceAddress,
    modbusAddress.accessLevel,
    1,
  );

  isDialogVisible.value = false;
}
</script>

<style scoped lang="scss"></style>
